// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleManager } from '../manager/article.manager';
import { ArticleVO } from '../model/vo/article.vo';
import { ArticleUtils } from '../util/article-utils';
import { ConnectionAreaVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleInfoVO, MetaElementInfoVO } from '@project/model/vo/project.vo';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { PropertyUtils } from '../util/property-utils';
import { ArticleConnectionPropertyDef } from '../constants/article-connection-property-def';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class ArticleService {

  constructor(private articleManager: ArticleManager,
              private connectionInfoService: ConnectionInfoService) { }

  public isMountingRailById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isMountingRail(article);
  }

  public isDrillingHoleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isDrillingHole(article);
  }

  public isWireBridgeById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isWireBridgeArticle(article);
  }

  public isRangeOnLineArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return this.isRangeOnLineArticle(article);
  }

  public isBlockingZoneById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isBlockingZone(article);
  }

  public isArticleDeletableById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isArticleDeletable(article);
  }

  public isCoverLabelStripArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isCoverLabelStripArticle(article);
  }

  public isEmptyZoneArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isEmptyZoneArticle(article);
  }

  public isCustomArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isCustomArticle(article);
  }

  public isSpeedwayById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isSpeedway(article);
  }

  public isCoverArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isCoverArticle(article);
  }

  public isFreeMountingById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isFreeMounting(article);
  }

  public isIO_750_753ArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isIO_750_753Article(article);
  }

  public isBusbarArticleById(articleId: number, articleVersion: number): boolean {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    return ArticleUtils.isBusbarArticle(article);
  }

  /**
   * Method returns articles which can be connected to the "parent" article by accepting area.
   *
   * @param article parent article
   * @param acceptingArea accepting connection to connect article to
   * @param searchArticles array of articles to check
   *
   * @return array of articles which can be connected
   */
  public findRequiredArticles(article: ArticleVO, acceptingArea: ConnectionAreaVO, searchArticles: ArticleVO[]): ArticleVO[] {
    if (!article || !acceptingArea || !searchArticles) {
      return [];
    }
    const result: ArticleVO[] = [];
    for (const searchArticle of searchArticles) {
      if (ConnectionInfoUtils.findRequiredConnection(searchArticle, article, acceptingArea)) {
        result.push(searchArticle);
      }
    }
    return result;
  }

  public isRangeOnLineArticle(article: ArticleVO, articleType: string = null): boolean {
    if (!article) {
      return false;
    }
    return (
      ConnectionInfoUtils.findRangeOnLineConnections(article.metaElements).length > 0 &&
      (!articleType || ArticleUtils.isArticleOfType(article, articleType))
    );
  }

  public createArticleInfo(articleGUID: string, article: ArticleVO, originalMetaElements: MetaElementVO[]): ArticleInfoVO {
    const articleInfo: ArticleInfoVO = new ArticleInfoVO();
    articleInfo.guid = articleGUID;
    articleInfo.identity = article.identity;
    articleInfo.version = article.version;
    articleInfo.attributes = [];
    articleInfo.metaElements = [];

    const originalConnectionArea: ConnectionAreaVO = ArticleUtils.findFirstConnectionArea(originalMetaElements);
    const originalTextureArea: TextureAreaVO = ArticleUtils.findFirstTextureArea(originalMetaElements);

    for (const metaElement of article.metaElements) {
      if (ConnectionInfoUtils.isStaticMetaElement(metaElement)) {
        continue;
      }
      const metaElementInfo: MetaElementInfoVO = new MetaElementInfoVO();
      metaElementInfo.name = metaElement.name;
      if (metaElement instanceof ConnectionAreaVO) {
        metaElementInfo.originalName = originalConnectionArea.name;
      } else if (metaElement instanceof TextureAreaVO) {
        metaElementInfo.originalName = originalTextureArea.name;
      }

      articleInfo.metaElements.push(metaElementInfo);
    }

    return articleInfo;
  }

  public isArticleWithoutFeatureProperties(article: ArticleVO): boolean {
    if (!article) {
      return true;
    }
    for (const metaElement of article.metaElements) {
      if (metaElement instanceof ConnectionAreaVO && (metaElement as ConnectionAreaVO).connectionSide === ConnectionAreaVO.SIDE_GIVING) {
        const featureProperties: PropertyVO[] = ConnectionInfoUtils.createFeaturePropertyList(metaElement as ConnectionAreaVO, article);
        if (featureProperties.length > 0) {
          return false;
        }
      }
    }
    return true;
  }

  public getLabelingArticleType(articleContainer: ArticleContainer): string {
    if (!ArticleUtils.isLabelingArticle(articleContainer.article)) {
      return null;
    }

    for (const identity of articleContainer.getCurrentArticleGivingAreaIds()) {
      const connection: Connection = articleContainer.findFirstConnectionByName(identity.metaElementName);
      if (!connection) {
        return null;
      }
      const features: PropertyVO[] = this.connectionInfoService.findFeaturePropertiesByConnection(connection);
      const values: string[] = PropertyUtils.getStringPropertyValues(features, ArticleConnectionPropertyDef.TYPE);
      for (const value of values) {
        return value;
      }
    }
    return null;
  }
}
